import React from 'react';
import { Link } from 'react-router-dom';
import type { TFunction } from 'i18next';
import history from 'helpers/history';
import { Tooltip, Button, BUTTON_TYPES } from '_atoms';
import { Priority } from '_atoms/Priority';
import type { PriorityValue } from '_atoms/Priority';
import { Typography } from '_atoms/Typography';
import { Status } from '_atoms/Status';
import type { TableColumnType } from '_atoms/Table/types';
import { getJobStatusType } from '_molecules/JobHeader/utils';
import { OPENING_STATUS_TYPES } from 'constants/opening';
import { dateFormatter } from 'helpers/dateFormatter';
import type { JobListLiteItem, Opening } from 'store/reducers/jobReducer';
import { ReactComponent as Sort } from 'dist/icons/arrow_ascending_descending.svg';
import { ReactComponent as CircularProgress } from 'dist/icons/loading.svg';

interface SortState {
  key: string;
  order: string;
  field: string;
}

interface LoadedOpeningsEntry {
  loaded?: boolean;
  loading?: boolean;
  openings?: Opening[];
}

export const headerCellRender =
  (title: string, field: string, key: string, sort: SortState, setSorting: (next: SortState) => void, _t?: TFunction) =>
  () => {
    const order = sort.key === key ? sort.order : null;
    const onClick = () => setSorting({ field, key, order: order === 'asc' ? 'desc' : 'asc' });
    return (
      <>
        {title}
        <Sort className={order ?? undefined} onClick={onClick} />
      </>
    );
  };

export const expandedRowRender = (openings: LoadedOpeningsEntry, job_uuid: string, t: TFunction) => {
  const toOpenings = (_job_uuid?: string) => history.push(`/jobs/${job_uuid}/show/openings?statuses=Open`);
  return openings.openings?.length ? (
    <>
      {openings.openings.map(
        (opening, idx) =>
          idx < 6 && (
            <Link
              key={`opening_${idx + 1}`}
              className="table-jobs__opening"
              to={`/jobs/${job_uuid}/show/openings?scroll=${opening.uuid}`}
            >
              <Priority priority={opening.priority as PriorityValue} />
              <Typography.Caption className="level">{opening.level}</Typography.Caption>
              <Typography.Caption className="expected-date">
                {t('translation:jobs.label_expected_on')} {dateFormatter(opening.expected)}
              </Typography.Caption>
              <div className="status">
                <Status type={OPENING_STATUS_TYPES[opening.status]}>{opening.status}</Status>
              </div>
              <Tooltip {...({ title: opening.resolution } as Record<string, unknown>)}>
                <Typography.Caption ellipsis className="resolution">
                  {opening.resolution as string}
                </Typography.Caption>
              </Tooltip>
            </Link>
          )
      )}
      {openings?.openings?.length > 6 && (
        <div className="table-jobs__opening__link">
          <Button type={BUTTON_TYPES.TEXT} onClick={() => toOpenings(job_uuid)}>
            {(t as (key: string, options: { count: number }) => string)('translation:jobs.button_more_openings', {
              count: openings.openings!.length - 6,
            })}
          </Button>
        </div>
      )}
    </>
  ) : (
    <Typography.Caption className="table-jobs__opening_empty">
      {t('translation:jobs.text_no_active_openings')}
      {openings?.loading && (
        <div className="loading-overflow">
          <CircularProgress />
        </div>
      )}
    </Typography.Caption>
  );
};

export const COLUMNS = (
  sort: SortState,
  setSorting: (next: SortState) => void,
  t: TFunction
): TableColumnType<JobListLiteItem>[] => [
  {
    title: headerCellRender(t('translation:jobs.column_job'), 'name', 'name', sort, setSorting),
    dataIndex: 'name',
    key: 'name',
    sorter: true,
    className: 'table-jobs__name',
    render: (name: string, { uuid }: { uuid: string }) => (
      <Tooltip label={name}>
        <Link to={`/jobs/${uuid}/show/steps`}>{name}</Link>
      </Tooltip>
    ),
  },
  {
    title: headerCellRender(
      t('translation:jobs.column_team_lead'),
      'team_lead_full_name',
      'team_lead_full_name',
      sort,
      setSorting
    ),
    dataIndex: 'team_lead_full_name',
    key: 'team_lead_full_name',
    width: 168,
    sorter: true,
    className: 'table-jobs__team-lead',
    render: (name: string) => <Tooltip label={name}>{name}</Tooltip>,
  },
  {
    title: headerCellRender(t('translation:jobs.column_job_status'), 'status', 'status', sort, setSorting, t),
    dataIndex: 'status',
    key: 'status',
    width: 138,
    sorter: true,
    className: 'table-jobs__status',
    render: (status: string) => <Status type={getJobStatusType(status)}>{status}</Status>,
  },
  {
    title: headerCellRender(
      t('translation:jobs.column_active_openings'),
      'openings_count',
      'openings_count',
      sort,
      setSorting,
      t
    ),
    dataIndex: 'openings_count',
    key: 'openings_count',
    width: 110,
    sorter: true,
    align: 'right',
    className: 'table-jobs__openings',
  },
  {
    title: headerCellRender(
      t('translation:jobs.column_active_candidates'),
      'candidates_count',
      'candidates_count',
      sort,
      setSorting,
      t
    ),
    dataIndex: 'candidates_count',
    key: 'candidates_count',
    width: 120,
    sorter: true,
    align: 'right',
    className: 'table-jobs__candidates-count',
  },
];

export const INITIAL_VALUES = {
  sorting: { key: 'status', order: 'asc', field: 'status' },
  limit: { value: 30, label: 30 },
  page: 1,
  offset: 0,
  itemsCount: 0,
};
